import { useCallback, useEffect, useState } from 'react';
import { Flex } from 'antd';

import {
  useAppSelector,
  useFindCountryTypeByType,
  useStatusFinderByNum,
  useUserPermissions,
} from '@Hooks/index';
import {
  useDownloadDocumentQuery,
  useFormListMutation,
} from '@Store/api/dmsApiSlice';
import { useGetHrmRequestFormQuery } from '@Store/api/basicHrmApiSlice';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { getDocument } from '@Features/utils/document';
import { User } from '@Types/index';
import { useGetUserInfoMutation } from '@Store/api/userApiSlice';
import { getOverseasSponsorsDictSelector } from '@Store/selectors/dictionarySelector';

import fieldSource from '../FieldSource';
import { getDocumentFolder } from '../../utils/document';
import { EditAction, FooterActions } from './components';
import styles from './view-mobile-form.module.css';

interface RequestViewMobileFormProps {
  onEdit: (recordKey: string) => void;
  onDeleteRequest: (requestNo: string) => void;
  onSubmitRequest: (requestNo: string) => void;
  onCancelRequest: (requestNo: string) => void;
}

const RequestViewMobileForm: React.FC<RequestViewMobileFormProps> = ({
  onEdit,
  onDeleteRequest,
  onSubmitRequest,
  onCancelRequest,
}) => {
  const { findStatus } = useStatusFinderByNum('Overseas');
  const { findCountryType } = useFindCountryTypeByType();

  const permissions = useUserPermissions();

  const requestId = useAppSelector(getModalRecordKey);
  const sponsors = useAppSelector(getOverseasSponsorsDictSelector);

  const { data } = useGetHrmRequestFormQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });
  const [getFormList] = useFormListMutation();
  const [getUser] = useGetUserInfoMutation();

  const [user, setUser] = useState<User | null>(null);
  const [documentId, setDocumentId] = useState('');

  const { data: imageURL } = useDownloadDocumentQuery(documentId || '', {
    skip: !documentId,
  });

  useEffect(() => {
    if (!data?.requestId) {
      return;
    }

    getUser({ empNo: data.empNumber })
      .unwrap()
      .then((userData) => {
        setUser(userData);
      });

    if (!data?.filepath) {
      return;
    }

    const documentFolderPath = getDocumentFolder(data.requestId);

    getFormList(documentFolderPath)
      .unwrap()
      .then((response) => {
        const matchedDocument = getDocument(response, data.filepath);

        if (matchedDocument) {
          setDocumentId(matchedDocument.documentId);
        }
      });
  }, [data]);

  const handleDownloadClick = useCallback((): void => {
    if (imageURL && data?.filepath) {
      const a = document.createElement('a');
      a.href = imageURL;
      a.download = data.filepath;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageURL, data?.filepath]);

  if (!data) return null;

  const statusRequest = findStatus(data?.reqStatusId)?.codeDesc || '';

  return (
    <Flex vertical>
      <div className={styles.viewMobileForm}>
        {fieldSource(
          data,
          findCountryType,
          findStatus,
          documentId,
          user,
          sponsors,
          handleDownloadClick
        ).map((item) => (
          <div className={styles.viewMobileItem} key={item.label}>
            <span>{item.label}</span>
            <span className={styles.viewMobileItemValue}>{item.value}</span>
          </div>
        ))}
      </div>
      <EditAction handleEdit={() => onEdit(requestId)} status={statusRequest} />
      <FooterActions
        handleDeleteRequest={() => onDeleteRequest(requestId)}
        handleSubmitRequest={() => onSubmitRequest(requestId)}
        handleCancelRequest={() => onCancelRequest(requestId)}
        disabledCancelForm={!permissions.can('Cancel', 'Overseas')}
        disabledDeleteForm={!permissions.can('Delete', 'Overseas')}
        disabledSubmitForm={!permissions.can('Submit', 'Overseas')}
        status={statusRequest}
      />
    </Flex>
  );
};

export default RequestViewMobileForm;
